import { useState, useRef, useCallback } from 'react';
import { useLocation } from 'wouter';
import { ShoppingCart, Eye, Zap } from 'lucide-react';
import { sfxClick, sfxHover } from '@/lib/soundFX';

interface Employee {
  id: number;
  name: string;
  roleTitle: string;
  departmentId: string;
  bio?: string;
  skills?: string[];
  price?: number;
  photoUrl?: string;
  availabilityStatus?: string;
}

interface EmployeeCardProps {
  employee: Employee;
  deptColor: string;
  onView: (emp: Employee) => void;
  onAddToCart: (emp: Employee) => void;
  index: number;
}

const DEPT_COLORS: Record<string, string> = {
  'small-business':   '#A855F7',
  'marketing':        '#FF0055',
  'sales':            '#00FF88',
  'customer-service': '#FF6B00',
  'operations':       '#00A6EB',
  'finance':          '#FFB800',
  'technology':       '#00FFFF',
  'enterprise':       '#7CFC00',
  'hospitality':      '#FF1493',
  'ecommerce':        '#FF8C00',
  'game-dev':         '#FF6B35',
  'spatial-xr':       '#BF5FFF',
  'geospatial':       '#00E5CC',
  'legal':            '#C8A96E',
  'healthcare':       '#00D4AA',
  'lending-realestate': '#4FC3F7',
  'worldbuilding':    '#FF9F1C',
  'product-management': '#7FDBFF',
};

const DEPT_ICONS: Record<string, string> = {
  'small-business':   '⬡',
  'marketing':        '◈',
  'sales':            '◆',
  'customer-service': '◎',
  'operations':       '⬢',
  'finance':          '◉',
  'technology':       '◇',
  'enterprise':       '⬡',
  'hospitality':      '◈',
  'ecommerce':        '◆',
  'game-dev':         '⬡',
  'spatial-xr':       '◈',
  'geospatial':       '◉',
  'legal':            '◆',
  'healthcare':       '◎',
  'lending-realestate': '⬢',
  'worldbuilding':    '◇',
  'product-management': '◈',
};

export default function EmployeeCard({ employee, deptColor, onView, onAddToCart, index }: EmployeeCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [, navigate] = useLocation();
  const [tiltX, setTiltX] = useState(0);
  const [tiltY, setTiltY] = useState(0);
  const [glowX, setGlowX] = useState(50);
  const [glowY, setGlowY] = useState(50);
  const cardRef = useRef<HTMLDivElement>(null);

  const isNew = employee.id > 108;

  const color = DEPT_COLORS[employee.departmentId] || deptColor || '#00A6EB';
  const icon = DEPT_ICONS[employee.departmentId] || '◆';

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setTiltX((y - 0.5) * -12);
    setTiltY((x - 0.5) * 12);
    setGlowX(x * 100);
    setGlowY(y * 100);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setTiltX(0);
    setTiltY(0);
    setGlowX(50);
    setGlowY(50);
  }, []);

  const price = employee.price ?? 297;
  const initials = employee.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div
      ref={cardRef}
      onMouseEnter={() => { setIsHovered(true); sfxHover(); }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        background: isHovered
          ? `linear-gradient(135deg, rgba(13,17,35,0.95), rgba(20,27,45,0.98))`
          : 'rgba(13,17,35,0.85)',
        border: `1px solid ${isHovered ? color : 'rgba(0,166,235,0.1)'}`,
        borderRadius: 4,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
        transform: isHovered
          ? `perspective(600px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateY(-6px) scale(1.02)`
          : 'perspective(600px) rotateX(0) rotateY(0) translateY(0) scale(1)',
        boxShadow: isHovered
          ? `0 20px 40px rgba(0,0,0,0.5), 0 0 30px rgba(${hexToRgb(color)}, 0.2), 0 0 60px rgba(${hexToRgb(color)}, 0.05)`
          : '0 4px 12px rgba(0,0,0,0.3)',
        clipPath: 'polygon(8px 0%, 100% 0%, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0% 100%, 0% 8px)',
        animationDelay: `${index * 0.05}s`,
        animationFillMode: 'both',
      }}
      className="emp-card-anim"
    >
      {/* Holographic shimmer on hover */}
      {isHovered && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle at ${glowX}% ${glowY}%, rgba(${hexToRgb(color)}, 0.18) 0%, rgba(${hexToRgb(color)}, 0.06) 40%, transparent 70%)`,
          pointerEvents: 'none',
          zIndex: 1,
          transition: 'background 0.1s ease',
        }} />
      )}
      {/* Rainbow holographic foil overlay */}
      {isHovered && (
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, opacity: 0.08,
          background: `linear-gradient(${tiltY * 5 + 135}deg, rgba(255,0,85,0.6) 0%, rgba(0,166,235,0.6) 33%, rgba(0,255,136,0.6) 66%, rgba(255,184,0,0.6) 100%)`,
          mixBlendMode: 'screen',
        }} />
      )}

      {/* Top accent bar */}
      <div style={{
        height: 2,
        background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
        opacity: isHovered ? 1 : 0.3,
        transition: 'opacity 0.25s ease',
      }} />

      {/* NEW badge */}
      {isNew && (
        <div style={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          fontFamily: 'Orbitron, monospace',
          fontWeight: 900,
          fontSize: '0.5rem',
          letterSpacing: '0.15em',
          color: '#0D1123',
          background: 'linear-gradient(90deg, #00FF88, #00E5CC)',
          padding: '2px 6px',
          borderRadius: 2,
          textShadow: 'none',
          boxShadow: '0 0 8px rgba(0,255,136,0.6), 0 0 16px rgba(0,255,136,0.3)',
          clipPath: 'polygon(3px 0%, 100% 0%, calc(100% - 3px) 100%, 0% 100%)',
          animation: 'newBadgePulse 2s ease-in-out infinite',
        }}>
          ◆ NEW
        </div>
      )}

      {/* Card content */}
      <div style={{ padding: '1rem', position: 'relative', zIndex: 2 }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.75rem' }}>
          {/* Avatar */}
          <div style={{
            width: 48, height: 48,
            borderRadius: 4,
            backgroundImage: employee.photoUrl
              ? `url(${employee.photoUrl})`
              : `linear-gradient(135deg, rgba(${hexToRgb(color)}, 0.3), rgba(${hexToRgb(color)}, 0.1))`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            border: `1px solid rgba(${hexToRgb(color)}, 0.4)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: isHovered ? `0 0 20px rgba(${hexToRgb(color)}, 0.5)` : `0 0 6px rgba(${hexToRgb(color)}, 0.1)`,
            transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
            clipPath: 'polygon(4px 0%, 100% 0%, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0% 100%, 0% 4px)',
            overflow: 'hidden',
          }}>
            {!employee.photoUrl && (
              <span style={{
                fontFamily: 'Orbitron, monospace',
                fontWeight: 900,
                fontSize: '0.9rem',
                color: color,
                textShadow: `0 0 8px ${color}`,
              }}>{initials}</span>
            )}
          </div>

          {/* Name & role */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: 'Orbitron, monospace',
              fontWeight: 700,
              fontSize: '0.84rem',
              color: '#E8F4FF',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              marginBottom: '0.2rem',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: '1.25',
            }}>
              {employee.name}
            </div>
            <div style={{
              fontFamily: 'Share Tech Mono, monospace',
              fontSize: '0.74rem',
              color: color,
              letterSpacing: '0.05em',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: '1.3',
            }}>
              {employee.roleTitle}
            </div>
          </div>

          {/* Dept icon */}
          <div style={{
            fontSize: '1rem',
            color: color,
            opacity: 0.6,
            textShadow: `0 0 8px ${color}`,
            flexShrink: 0,
          }}>
            {icon}
          </div>
        </div>

        {/* Skills tags */}
        {employee.skills && employee.skills.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '0.75rem' }}>
            {employee.skills.slice(0, 3).map((skill, si) => (
              <span key={si} style={{
                fontFamily: 'Share Tech Mono, monospace',
                fontSize: '0.65rem',
                letterSpacing: '0.08em',
                color: 'rgba(232,244,255,0.5)',
                background: 'rgba(0,166,235,0.06)',
                border: '1px solid rgba(0,166,235,0.1)',
                padding: '2px 6px',
                borderRadius: 2,
                textTransform: 'uppercase',
              }}>
                {skill}
              </span>
            ))}
          </div>
        )}

        {/* Footer: price + actions */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{
              fontFamily: 'Orbitron, monospace',
              fontWeight: 900,
              fontSize: '0.9rem',
              color: '#FFB800',
              textShadow: '0 0 8px rgba(255,184,0,0.4)',
            }}>
              ${price.toLocaleString()}
            </div>
            <div style={{
              fontFamily: 'Share Tech Mono, monospace',
              fontSize: '0.45rem',
              color: 'rgba(232,244,255,0.3)',
              letterSpacing: '0.1em',
            }}>
              /DEPLOYMENT
            </div>
          </div>

          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={(e) => { e.stopPropagation(); sfxClick(); onView(employee); }}
              onDoubleClick={(e) => { e.stopPropagation(); sfxClick(); navigate(`/agent/${employee.id}`); }}
              style={{
                background: 'rgba(0,166,235,0.1)',
                border: `1px solid rgba(0,166,235,0.3)`,
                borderRadius: 3,
                padding: '5px 8px',
                cursor: 'pointer',
                color: '#00A6EB',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                fontFamily: 'Orbitron, monospace',
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
              transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { sfxHover();
                (e.currentTarget as HTMLElement).style.background = 'rgba(0,166,235,0.2)';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 0 10px rgba(0,166,235,0.3)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(0,166,235,0.1)';
                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
              }}
            >
              <Eye size={10} />
              DOSSIER
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); sfxClick(); onAddToCart(employee); }}
              style={{
                background: `rgba(${hexToRgb(color)}, 0.15)`,
                border: `1px solid rgba(${hexToRgb(color)}, 0.4)`,
                borderRadius: 3,
                padding: '5px 8px',
                cursor: 'pointer',
                color: color,
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                fontFamily: 'Orbitron, monospace',
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
              transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { sfxHover();
                (e.currentTarget as HTMLElement).style.background = `rgba(${hexToRgb(color)}, 0.3)`;
                (e.currentTarget as HTMLElement).style.boxShadow = `0 0 10px rgba(${hexToRgb(color)}, 0.3)`;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = `rgba(${hexToRgb(color)}, 0.15)`;
                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
              }}
            >
              <ShoppingCart size={10} />
              DEPLOY
            </button>
          </div>
        </div>
      </div>

      {/* Bottom scan line animation */}
      {isHovered && (
        <div style={{
          position: 'absolute',
          bottom: 0, left: 0, right: 0,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
          animation: 'scanlineMove 1.5s linear infinite',
        }} />
      )}
    </div>
  );
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0,166,235';
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}
